import apiClient from '@/lib/api-client';
import { Module, Mutation, VuexModule, Action } from 'vuex-module-decorators';
import {
    CreateEventFlowItem,
    EventFlowItem,
    EventFlowItemRequest,
    EventFlowItemResponse,
    UpdateEventFlowItem,
} from '@/model/event-flow/EventFlow';
import { ApiError } from '@/model/ApiError';
import { createDateFromUTCString } from '@/lib/date-util';
import { EventFlowSettingsResponse } from '@/model/Event';

@Module({ namespaced: true, preserveState: false })
export default class EventFlowStorage extends VuexModule {
    eventFlowItems: EventFlowItem[] = [];
    eventFlowDate: Date | null = null;

    eventFlowSettings: EventFlowSettingsResponse | null = null;

    error: ApiError | null = null;

    @Mutation
    setEventFlowItems(eventFlowItems: EventFlowItem[]): void {
        eventFlowItems.forEach((eventFlowItem: EventFlowItem) => {
            eventFlowItem.startedAt = createDateFromUTCString(
                eventFlowItem.startedAt.toString(),
            );
            eventFlowItem.endedAt = createDateFromUTCString(
                eventFlowItem.endedAt.toString(),
            );
        });
        this.eventFlowItems = eventFlowItems;
    }

    @Mutation
    setItem(eventFlowItem: EventFlowItem): void {
        const oldEventFlowItem = this.eventFlowItems.find(
            (item) => item.id === eventFlowItem.id,
        );

        Object.assign(oldEventFlowItem, eventFlowItem);
    }

    @Mutation
    setEventFlowDate(date: Date | null): void {
        this.eventFlowDate = date;
    }

    @Mutation
    setError(error: ApiError | null): void {
        this.error = error;
    }

    @Mutation
    setEventFlowSettings(settings: EventFlowSettingsResponse): void {
        settings.selectedStartDate = createDateFromUTCString(
            settings.selectedStartDate.toString(),
        );
        settings.selectedEndDate = createDateFromUTCString(
            settings.selectedEndDate.toString(),
        );
        this.eventFlowSettings = settings;
    }

    @Action
    fetchEventFlowItems(
        request: EventFlowItemRequest,
    ): Promise<void | EventFlowItemResponse> {
        this.context.commit('setError', null);

        return apiClient
            .post<void, EventFlowItemResponse>(
                `/events/${request.eventId}/items/filters`,
                request,
            )
            .then((data: EventFlowItemResponse) => {
                this.context.commit('setEventFlowItems', data.eventItemDtoList);
                this.context.commit('setEventFlowDate', data.filterDate);
                return data;
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action({ commit: 'setItem' })
    updateItem(request: UpdateEventFlowItem): Promise<void | EventFlowItem> {
        return apiClient
            .put<EventFlowItem, EventFlowItem>(
                `/events/${request.eventId}/items/${request.item.id}`,
                request.item,
            )
            .then(() => {
                this.context.commit('setError', null);
                return request.item;
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    createItem(request: CreateEventFlowItem): Promise<void | EventFlowItem> {
        return apiClient
            .post<EventFlowItem, EventFlowItem>(
                `/events/${request.eventId}/items`,
                request.item,
            )
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }
    @Action
    deleteItem(request: CreateEventFlowItem): Promise<void | EventFlowItem> {
        return apiClient
            .delete(`/events/${request.eventId}/items/${request.item.id}`)
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchEventFlowSettings(eventId: number): Promise<void> {
        return apiClient
            .get(`/events/${eventId}/event-settings`)
            .then((data) => {
                this.context.commit('setEventFlowSettings', data);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    get getEventFlowSettings(): EventFlowSettingsResponse | null {
        return this.eventFlowSettings;
    }

    get getEventFlowItems(): EventFlowItem[] | undefined {
        return this.eventFlowItems;
    }

    get getEventFlowDate(): Date | null {
        return this.eventFlowDate;
    }

    get getError(): ApiError | null {
        return this.error;
    }
}
